import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  Dialog,
  IconCritical,
  Stack,
  Text,
} from 'braid-design-system';
import { useEffect, useState } from 'react';

import { useAnalytics } from '../../context/AnalyticsProvider';
import type { PaymentMethodType } from '../../entities/enums';
import translations from '../../locales.vocab';

import CountdownTimer from './CountdownTimer';

import {
  qrImageWrapper,
  qrImageExpired,
  expireExclamation,
  countdownTimer,
} from './QRModal.css';

const QRModal = ({
  id,
  url,
  expiry,
  isModalOpen,
  onCloseDialog,
  paymentMethod,
}: {
  id: string;
  url: string;
  expiry: string;
  isModalOpen: boolean;
  onCloseDialog: (isExpiredFromModal: boolean) => void;
  paymentToken: string;
  paymentMethod: PaymentMethodType;
}) => {
  const [isExpired, setIsExpired] = useState(false);

  const { t } = useTranslations(translations);
  const { sendLinkQRDisplayEvent, sendViewAlertViewedEvent } = useAnalytics();

  useEffect(() => {
    sendLinkQRDisplayEvent(paymentMethod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      id={id}
      title=""
      onClose={() => {
        onCloseDialog(isExpired);
        setIsExpired(false);
      }}
      open={isModalOpen}
      width="small"
      closeLabel={t('label.close')}
    >
      <Stack space="large" align="center">
        <Text size="large" weight="strong">
          {t('qr.modal.header')}
        </Text>

        <Text align="center" tone="secondary">
          {t('qr.modal.description')}
        </Text>

        <Text align="center" tone="secondary">
          {t('payment.loading.stripe.message')}.
        </Text>

        <Stack space="small" align="center">
          <div className={qrImageWrapper} data-expire={isExpired}>
            <img
              alt="QR code"
              className={isExpired ? qrImageExpired : ''}
              src={url}
              width={200}
              height={200}
            />
            {isExpired && (
              <div className={expireExclamation}>
                <IconCritical tone="critical" />
              </div>
            )}
          </div>

          {isExpired ? (
            <Alert tone="critical">
              <Text id="stripeFormValidMsg">{t('qr.expire.error')}</Text>
            </Alert>
          ) : (
            <Box padding="none">
              <Text align="center">
                <span className={countdownTimer}>
                  {t('expiry.timer.error')}
                </span>
                <CountdownTimer
                  milliseconds={Number(new Date()) + Number(expiry)}
                  onComplete={() => {
                    setIsExpired(true);
                    sendViewAlertViewedEvent(
                      [
                        {
                          type: 'banner',
                          title: t('qr.expire.error'),
                          messageCode: 'purchase_qr_code_expired',
                        },
                      ],
                      'HkFps',
                    );
                  }}
                />
              </Text>
            </Box>
          )}
        </Stack>

        <Box />
      </Stack>
    </Dialog>
  );
};

export default QRModal;
